import type { SectionProps } from "../context";
import { domId, field } from "../context";
import { CARD_HOVER, Chip, Container, Img, MultiLine, OrderLink, Price, STACK, STAGGER, SectionHeader, SectionShell, cx } from "../ui";

const pesan =
  "inline-flex min-h-10 items-center gap-1.5 rounded-site-button border border-site-border px-3.5 text-sm font-semibold text-site-text transition hover:border-site-primary hover:bg-site-primary hover:text-site-on-primary";

export function Menu({ node, ctx }: SectionProps<"menu">) {
  const c = node.content;
  const list = node.variant === "list";
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} align={list ? "center" : "left"} className={list ? "mx-auto" : undefined} />
        <div className={cx(STACK, list ? "grid gap-x-16 gap-y-14 @4xl:grid-cols-2" : "space-y-14 @3xl:space-y-16")}>
          {c.categories.map((cat, ci) => {
            const catId = domId(ctx, `${node.anchor}-${ci}`);
            return (
              <div key={ci} role="group" aria-labelledby={catId}>
                <div className="mb-6 flex items-baseline gap-4">
                  <h3 id={catId} className="baze-heading text-2xl leading-tight @3xl:text-[1.75rem]" {...field(ctx, `content.categories.${ci}.name`)}>
                    {cat.name}
                  </h3>
                  <span aria-hidden="true" className="h-px flex-1 bg-site-border" />
                  <span className="text-sm text-site-muted tabular-nums">{cat.items.length} menu</span>
                </div>
                {list ? (
                  <ul className={cx(STAGGER, "space-y-6")}>
                    {cat.items.map((item, ii) => {
                      const p = `content.categories.${ci}.items.${ii}`;
                      return (
                        <li key={ii} className="flex gap-4">
                          {item.image ? (
                            <div className="size-16 shrink-0 overflow-hidden rounded-site-sm bg-site-surface @3xl:size-20">
                              <Img ctx={ctx} image={item.image} sizes="80px" target={480} path={`${p}.image`} />
                            </div>
                          ) : null}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-3">
                              <h4 className="font-semibold text-site-text @3xl:text-lg" {...field(ctx, `${p}.name`)}>
                                {item.name}
                              </h4>
                              <span aria-hidden="true" className="min-w-6 flex-1 translate-y-[-0.3em] border-b border-dotted border-site-border" />
                              <Price value={item.price} className="text-site-text" />
                            </div>
                            <MultiLine ctx={ctx} text={item.description} path={`${p}.description`} className="mt-1 text-sm leading-relaxed text-site-muted" />
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              {item.tags?.map((t, ti) => (
                                <Chip key={ti} className="py-0.5">
                                  {t}
                                </Chip>
                              ))}
                              <OrderLink
                                ctx={ctx}
                                message={c.orderMessage}
                                produk={item.name}
                                className="inline-flex min-h-9 items-center gap-1.5 text-sm font-semibold text-site-primary underline-offset-4 hover:underline"
                              />
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <ul className={cx(STAGGER, "grid gap-4 @xl:grid-cols-2 @4xl:grid-cols-3 @3xl:gap-6")}>
                    {cat.items.map((item, ii) => {
                      const p = `content.categories.${ci}.items.${ii}`;
                      return (
                        <li key={ii} className={cx("group flex flex-col overflow-hidden rounded-site-md border border-site-border bg-site-surface shadow-site-sm", CARD_HOVER)}>
                          {item.image ? (
                            <div className="aspect-[4/3] overflow-hidden bg-site-bg">
                              <Img ctx={ctx} image={item.image} sizes="(min-width: 1024px) 360px, (min-width: 576px) 50vw, 100vw" path={`${p}.image`} />
                            </div>
                          ) : null}
                          <div className="flex flex-1 flex-col p-5 @3xl:p-6">
                            <div className="flex items-start justify-between gap-4">
                              <h4 className="text-lg leading-snug font-semibold text-site-text" {...field(ctx, `${p}.name`)}>
                                {item.name}
                              </h4>
                              <Price value={item.price} className="shrink-0 pt-0.5 text-site-text" />
                            </div>
                            <MultiLine ctx={ctx} text={item.description} path={`${p}.description`} className="mt-2 text-sm leading-relaxed text-site-muted" />
                            <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5">
                              <div className="flex flex-wrap gap-1.5">
                                {item.tags?.map((t, ti) => (
                                  <Chip key={ti} className="py-0.5">
                                    {t}
                                  </Chip>
                                ))}
                              </div>
                              <OrderLink ctx={ctx} message={c.orderMessage} produk={item.name} className={pesan} />
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </Container>
    </SectionShell>
  );
}
